import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Edit, Mail, Calendar, DollarSign } from 'lucide-react';
import { format } from 'date-fns';

const TeamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: member, isLoading } = useQuery({
    queryKey: ['team-member', id],
    queryFn: async () => {
      // Mock data - replace with actual API call
      return {
        id: Number(id),
        name: 'Roman Siromskyi',
        email: 'leowebmark@gmail.com',
        avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Roman',
        role: 'admin',
        department: 'Management',
        status: 'active',
        hourly_rate: 150,
        created_at: new Date().toISOString(),
        stats: {
          total_hours: 520,
          projects_assigned: 12,
          tasks_completed: 145,
          utilization: 92,
        },
        projects: [
          { id: 1, name: 'LWM PM System', status: 'active', role: 'Lead Developer' },
          { id: 2, name: 'Barcelona E-commerce', status: 'active', role: 'Tech Lead' },
          { id: 3, name: 'Fixaro Platform', status: 'in_progress', role: 'Architect' },
        ],
        recentTasks: [
          { id: 1, title: 'Implement authentication', status: 'done', project: 'LWM PM System' },
          { id: 2, title: 'Setup database schema', status: 'in_progress', project: 'LWM PM System' },
          { id: 3, title: 'Code review', status: 'done', project: 'Barcelona E-commerce' },
        ],
        timeEntries: [
          { date: '2025-10-02', project: 'LWM PM System', hours: 8, description: 'Backend development' },
          { date: '2025-10-01', project: 'Barcelona E-commerce', hours: 6, description: 'Code review and refactoring' },
          { date: '2025-09-30', project: 'Fixaro Platform', hours: 7, description: 'Architecture planning' },
        ],
      };
    },
  });

  const roleColors: Record<string, string> = {
    admin: 'bg-purple-500/10 text-purple-500',
    project_manager: 'bg-blue-500/10 text-blue-500',
    developer: 'bg-green-500/10 text-green-500',
    designer: 'bg-pink-500/10 text-pink-500',
    qa: 'bg-orange-500/10 text-orange-500',
  };

  const statusColors: Record<string, string> = {
    active: 'bg-green-500/10 text-green-500',
    inactive: 'bg-gray-500/10 text-gray-500',
    on_leave: 'bg-yellow-500/10 text-yellow-500',
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="p-6">
        <p>Member not found</p>
      </div>
    );
  }

  const initials = member.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/team')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Team Member Details</h1>
            <p className="text-muted-foreground mt-1">View and manage member information</p>
          </div>
        </div>
        <Link to={`/team/${id}/edit`}>
          <Button>
            <Edit className="h-4 w-4 mr-2" />
            Edit Member
          </Button>
        </Link>
      </div>

      {/* Member Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={member.avatar_url} alt={member.name} />
              <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="text-2xl font-bold">{member.name}</h2>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={roleColors[member.role] || ''}>{member.role}</Badge>
                  <Badge className={statusColors[member.status] || ''}>{member.status}</Badge>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{member.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span>${member.hourly_rate}/hr</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Joined {format(new Date(member.created_at), 'MMM d, yyyy')}</span>
                </div>
              </div>
              {member.department && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Department: </span>
                  <span className="font-medium">{member.department}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Hours</p>
              <p className="text-3xl font-bold mt-2">{member.stats.total_hours}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Projects</p>
              <p className="text-3xl font-bold mt-2">{member.stats.projects_assigned}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Tasks Completed</p>
              <p className="text-3xl font-bold mt-2">{member.stats.tasks_completed}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Utilization</p>
              <p className="text-3xl font-bold mt-2">{member.stats.utilization}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="projects" className="w-full">
        <TabsList>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="time">Time Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Assigned Projects</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {member.projects.map((project) => (
                  <div
                    key={project.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div>
                      <p className="font-semibold">{project.name}</p>
                      <p className="text-sm text-muted-foreground">{project.role}</p>
                    </div>
                    <Badge>{project.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {member.recentTasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div>
                      <p className="font-semibold">{task.title}</p>
                      <p className="text-sm text-muted-foreground">{task.project}</p>
                    </div>
                    <Badge>{task.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="time" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Time Entries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {member.timeEntries.map((entry, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-semibold">{entry.project}</p>
                      <p className="text-sm text-muted-foreground">{entry.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(entry.date), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{entry.hours}h</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TeamDetail;
